import { useCallback } from 'react';
import api from '../redux/api';

export interface ProfileTrackingData {
  clinicianId?: string;
  clinicianName?: string;
  clinicianSlug?: string;
  matchType?: string;
  position?: string;
  therapistIndex?: number;
  viewType?: string;
  [key: string]: any;
}

export interface ProfileTrackingOptions {
  buttonType: string;
  additionalData?: ProfileTrackingData;
  searchFilters?: {
    specialty?: string;
    location?: string;
    insurance?: string;
  };
  practiceId?: string;
}

/**
 * Custom hook for tracking profile views and clicks
 * Sends comprehensive tracking data to the backend for analytics
 */
export const useProfileTracking = (searchFilters?: {
  specialty?: string;
  location?: string;
  insurance?: string;
}, practiceId?: string) => {
  // Helper function to get practiceId from localStorage if not provided
  const getPracticeId = useCallback(() => {
    if (practiceId) return practiceId;
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.["custom:id_practice"] || user?.practice_id || null;
    } catch (error) {
      console.error('Error getting practiceId from localStorage:', error);
      return null;
    }
  }, [practiceId]);

  const trackProfileClick = useCallback(async (options: ProfileTrackingOptions) => {
    try {
      const { buttonType, additionalData = {}, practiceId: optionsPracticeId } = options;
      
      // Get practiceId from options, hook parameter, or localStorage
      const finalPracticeId = optionsPracticeId || getPracticeId();
      
      if (!finalPracticeId) {
        console.warn('⚠️ Practice ID not found. Tracking may not be recorded correctly.');
      }
      
      const trackingData = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        practiceId: finalPracticeId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sessionId: sessionStorage.getItem('sessionId') || `session_${Date.now()}`,
        activity_type: buttonType,
        buttonType: buttonType,
        action_type: buttonType,
        ...additionalData
      };
      
      
      const response = await api.post('/profile/profile-click', trackingData);
      
      if (response.status === 200 || response.status === 201) {
        return { success: true };
      } else {
        console.error(`Failed to track ${options.buttonType} button click:`, response.status);
        return { success: false, error: `HTTP ${response.status}` };
      }
    } catch (error) {
      console.error(`Error tracking ${options.buttonType} button click:`, error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }, [searchFilters, getPracticeId]);

  return {
    trackProfileClick
  };
};

